import { apiRequest } from "./apiClient";

/**
 * Billing / entitlements client.
 *
 * Mirrors `server/src/billing`. Everything here is advisory for the UI — the
 * server re-checks every entitlement, so a tampered client gains nothing.
 */

export type PlanTier = "FREE" | "BASIC" | "PREMIUM" | "PLATINUM" | "ULTRA_PRO";
export type BillingCurrency = "LKR" | "USD";

/**
 * Basic Pass is a single payment; Premium, Platinum and Ultra Pro are monthly
 * PayHere subscriptions that re-bill until cancelled.
 */
export type BillingPeriod = "ONE_TIME" | "MONTHLY";

export type SubscriptionStatus =
  | "ACTIVE"
  | "CANCELLED"
  | "PAST_DUE"
  | "EXPIRED";
export type TemplateAccess =
  | "PREVIEW_ONLY"
  | "CLASSIC_ONLY"
  | "PREMIUM_15"
  | "ALL_16";

export type PlanFeatures = {
  canReorderSections: boolean;
  hasAtsAccess: boolean;
  hasPortfolioAccess: boolean;
  hasAiCareerAgent: boolean;
  hasCommercialLicense: boolean;
  aiAgentMonthlyQuota: number;
};

export type Plan = {
  tier: PlanTier;
  name: string;
  audience: string;
  purchasable: boolean;
  billingPeriod: BillingPeriod;
  /** Price in the resolved currency, per billing period. */
  price: number;
  priceLKR: number;
  priceUSD: number;
  /** Percent off from a live auto-apply promotion; 0 when none applies. */
  discountPercent: number;
  /** Price after the promotion, in the resolved currency; null when none. */
  discountedPrice: number | null;
  downloadCredits: number | "UNLIMITED";
  templateAccess: TemplateAccess;
  features: PlanFeatures;
  highlights: string[];
};

/** Live auto-apply promotion attached to the catalog, if any. */
export type CatalogOffer = {
  id: string;
  code: string;
  headline: string;
  description: string;
  discountPercent: number;
  appliesToTiers: PlanTier[];
  autoApply: boolean;
  endsAt: string;
};

export type PlanCatalog = {
  currency: BillingCurrency;
  detectedCountry: string;
  currencyWasOverridden: boolean;
  activeOffer: CatalogOffer | null;
  plans: Plan[];
};

export type AiAgentQuota = {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string | null;
};

export type SubscriptionSummary = {
  tier: PlanTier | null;
  planName: string | null;
  status: SubscriptionStatus | null;
  /** End of the period already paid for; access runs until then. */
  currentPeriodEnd: string | null;
  isActive: boolean;
  /** Cancelled, but still inside the paid period. */
  cancelAtPeriodEnd: boolean;
  chargeCount: number;
  /** Tier chosen for after the current period, if a downgrade is scheduled. */
  pendingTier: PlanTier | null;
  pendingPlanName: string | null;
  pendingEffectiveAt: string | null;
};

/** What the customer may do with a plan, given what they already hold. */
export type PlanAction =
  | "BUY"
  | "SUBSCRIBE"
  | "CURRENT"
  | "UPGRADE"
  | "DOWNGRADE"
  | "UNAVAILABLE";

export type PlanActionInfo = {
  tier: PlanTier;
  action: PlanAction;
  /** When the change lands, or null when there is nothing to do. */
  effective: "IMMEDIATE" | "PERIOD_END" | null;
  reason?: string;
};

export type EntitlementSummary = {
  planTier: PlanTier;
  planName: string;
  billingPeriod: BillingPeriod;
  /** Total spendable across both buckets. */
  downloadCredits: number;
  creditBreakdown: {
    /** Bought outright via Basic Pass — never expire. */
    purchased: number;
    /** This cycle's allowance — reset each renewal, lost when it lapses. */
    subscriptionAllowance: number;
    bonus?: number;
  };
  hasUnlimitedDownloads: boolean;
  premiumDesignDownloads?: number;
  subscription: SubscriptionSummary;
  /** Per-plan actions — drives which button each pricing card shows. */
  availableActions: PlanActionInfo[];
  templateAccess: TemplateAccess;
  features: Omit<PlanFeatures, "aiAgentMonthlyQuota">;
  aiAgent: AiAgentQuota;
  totals: { creditsPurchased: number; downloadsUsed: number };
  lastPurchaseAt: string | null;
};

export type DownloadEligibility =
  | { templateKey: string; allowed: true; requiresCreditDeduction: boolean }
  | {
      templateKey: string;
      allowed: false;
      reason: "TEMPLATE_TIER_TOO_LOW" | "NO_DOWNLOAD_CREDITS";
      message: string;
      requiredTier: PlanTier;
      currentTier?: PlanTier;
      remainingCredits?: number;
      suggestedTiers: PlanTier[];
    };

/** Signed form the browser POSTs straight to PayHere. */
export type CheckoutSession = {
  orderId: string;
  planTier: PlanTier;
  currency: BillingCurrency;
  /** Amount actually charged, after any promotion. */
  amount: number;
  listAmount: number;
  discountPercent: number;
  promoCode: string | null;
  checkout: {
    action: string;
    sandbox: boolean;
    fields: Record<string, string>;
  };
};

/**
 * Order lifecycle. Customers only ever see PENDING / SUCCESS / FAILED on their
 * profile; the rest are reconciliation states shown in the admin ledger.
 */
export type TransactionStatus =
  | "PENDING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELED"
  | "CHARGEDBACK"
  /** Checkout was never completed — aged out of PENDING. */
  | "ABANDONED";

export type OrderStatus = {
  orderId: string;
  planTier: PlanTier;
  planName: string;
  currency: BillingCurrency;
  amount: number;
  status: TransactionStatus;
  entitlementsApplied: boolean;
  creditsGranted: number;
  paymentId: string | null;
  paymentMethod: string | null;
  createdAt: string;
};

export type CheckoutRequest = {
  planTier: PlanTier;
  /** Selected CV design, so incompatible purchases are refused before payment. */
  templateKey?: string;
  /** Omit to let the server route by country (LK → LKR, else USD). */
  currency?: BillingCurrency;
  /**
   * Promo code from an offer email. Omit to let the server apply any live
   * auto-apply promotion on its own.
   */
  promoCode?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  /** Builder path to return to after payment, e.g. "/builder/123". */
  returnPath?: string;
};

// ─── Currency preference ────────────────────────────────────────────────────

const CURRENCY_PREFERENCE_KEY = "rx.billing.currency";

/**
 * The currency the visitor last picked with the toggle. Without this every
 * pricing surface re-ran server detection on mount, so a choice made on
 * /pricing was forgotten by the paywall a moment later.
 */
export const getPreferredCurrency = (): BillingCurrency | undefined => {
  try {
    const stored = localStorage.getItem(CURRENCY_PREFERENCE_KEY);
    return stored === "LKR" || stored === "USD" ? stored : undefined;
  } catch {
    return undefined;
  }
};

export const setPreferredCurrency = (currency: BillingCurrency): void => {
  try {
    localStorage.setItem(CURRENCY_PREFERENCE_KEY, currency);
  } catch {
    // Storage unavailable (private mode, etc.) — detection still works.
  }
};

// ─── Reads ──────────────────────────────────────────────────────────────────

/**
 * Plan catalog in the given currency, else the visitor's saved preference,
 * else whatever the server detects from the request.
 */
export const getPlans = (currency?: BillingCurrency) => {
  const resolved = currency ?? getPreferredCurrency();
  return apiRequest<PlanCatalog>(
    resolved ? `/billing/plans?currency=${resolved}` : "/billing/plans",
  );
};

export const getEntitlements = () =>
  apiRequest<EntitlementSummary>("/billing/entitlements");

export const checkDownloadEligibility = (templateKey: string) =>
  apiRequest<DownloadEligibility>("/billing/download-eligibility", {
    method: "POST",
    body: JSON.stringify({ templateKey }),
  });

export const getOrderStatus = (orderId: string) =>
  apiRequest<OrderStatus>(`/billing/orders/${orderId}`);

export const listTransactions = (limit = 25) =>
  apiRequest<OrderStatus[]>(`/billing/transactions?limit=${limit}`);

/**
 * Retire an unpaid checkout right away.
 *
 * Called when the gateway sends the customer to our cancel page: without it the
 * order sits at PENDING for an hour and reads as a payment still in progress.
 * The server only touches untouched PENDING orders, so a payment that settles
 * anyway is still provisioned by the webhook.
 */
export const abandonCheckout = (orderId: string) =>
  apiRequest<{ abandoned: boolean; status: TransactionStatus }>(
    `/billing/orders/${orderId}/abandon`,
    { method: "POST" },
  );

/**
 * Stop a monthly subscription renewing.
 *
 * Access continues to `accessUntil` — the end of the period already paid for.
 */
export const cancelSubscription = () =>
  apiRequest<{ cancelled: true; accessUntil: string | null }>(
    "/billing/subscription/cancel",
    { method: "POST" },
  );

/** An upgrade returns a checkout to complete; a downgrade is just scheduled. */
export type PlanChangeResult =
  | { kind: "UPGRADE"; checkout: CheckoutSession }
  | {
      kind: "DOWNGRADE";
      pendingTier: PlanTier;
      effectiveAt: string | null;
      message: string;
    };

/**
 * Switch tier.
 *
 * Upgrades take effect as soon as the returned checkout is paid; downgrades
 * are scheduled for the end of the period already paid for.
 */
export const changePlan = (planTier: PlanTier, returnPath?: string) =>
  apiRequest<PlanChangeResult>("/billing/subscription/change", {
    method: "POST",
    body: JSON.stringify({ planTier, returnPath }),
  });

// ─── Checkout ───────────────────────────────────────────────────────────────

export const createCheckout = (payload: CheckoutRequest) =>
  apiRequest<CheckoutSession>("/billing/checkout", {
    method: "POST",
    body: JSON.stringify(payload),
  });

/**
 * Hand the browser over to PayHere.
 *
 * PayHere's checkout is a form POST, not a link, so the signed fields are
 * submitted through a throwaway form. The page navigates away; the entitlement
 * grant happens server-side via the webhook, and the return page polls
 * `getOrderStatus` until `entitlementsApplied` flips.
 */
export const redirectToPayHere = (session: CheckoutSession): void => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = session.checkout.action;
  form.style.display = "none";

  for (const [name, value] of Object.entries(session.checkout.fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};

/** One call: create the order, then hand off to the gateway. */
export const startCheckout = async (
  payload: CheckoutRequest,
): Promise<CheckoutSession> => {
  const session = await createCheckout(payload);
  redirectToPayHere(session);
  return session;
};

// ─── Paywall errors ─────────────────────────────────────────────────────────

/** Shape of the 403 body every gated endpoint returns. */
export type PaywallError = {
  code:
    | "FEATURE_LOCKED"
    | "TEMPLATE_TIER_TOO_LOW"
    | "NO_DOWNLOAD_CREDITS"
    | "AI_AGENT_QUOTA_EXCEEDED"
    | "UNKNOWN";
  message: string;
  feature?: "REORDER_SECTIONS" | "ATS_ENGINE" | "PORTFOLIO" | "AI_CAREER_AGENT";
  requiredTier?: PlanTier;
  currentTier?: PlanTier;
  remainingCredits?: number;
  /** Plans to show in the upgrade modal, cheapest first. */
  suggestedTiers?: PlanTier[];
  quota?: AiAgentQuota;
};

/**
 * Read a paywall payload out of a 403 response.
 *
 * Nest wraps thrown objects, so the fields may sit at the top level or under
 * `message`; both shapes are handled, with a safe fallback either way.
 */
export const parsePaywallError = async (
  response: Response,
): Promise<PaywallError> => {
  const fallback: PaywallError = {
    code: "UNKNOWN",
    message: "This feature requires an upgrade.",
  };

  try {
    const body: unknown = await response.json();
    if (!body || typeof body !== "object") return fallback;

    const record = body as Record<string, unknown>;
    const nested =
      record.message && typeof record.message === "object"
        ? (record.message as Record<string, unknown>)
        : record;

    return {
      ...fallback,
      ...(nested as Partial<PaywallError>),
      message:
        typeof nested.message === "string" ? nested.message : fallback.message,
    };
  } catch {
    return fallback;
  }
};

/** True when an unknown error is a paywall rejection worth upselling on. */
export const isPaywallError = (value: unknown): value is PaywallError =>
  typeof value === "object" &&
  value !== null &&
  "code" in value &&
  typeof (value as { code: unknown }).code === "string";
